import React from "react"
import { Button, Divider, Flex, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import { ConfirmModal } from "ui/ConfirmModal"
import { numberToCurrency } from "helpers/string"
import { useCancelBookingModal } from "./useCancelBookingModal"
import BookingCard from "./BookingCard"
import ReasonSection from "./ReasonSection"
import PaymentBreakdown from "./PaymentBreakdown"
import styles from "./index.module.css"

const CancelBookingModal = ({ open, onCancel, onConfirm, bookings }) => {
  const { t } = useTranslation()
  const booking = bookings?.[0]

  const {
    refundType, partialAmount, partialAmountError,
    selectedReason, setSelectedReason, reasonError, setReasonError,
    otherReason, setOtherReason, otherReasonError, setOtherReasonError,
    breakdownData, breakdownLoading, submitLoading,
    cancelReasons, reasonsLoading, reasonsFailed,
    isOtherSelected, isRefundDisabled,
    confirmOpen, setConfirmOpen,
    handlePartialAmountChange, handleRadioChange, handleSubmitClick, handleConfirmClick
  } = useCancelBookingModal(booking, onConfirm)

  const confirmInfoParams = {
    title: booking?.title ? `<b>${booking.title}</b>` : "",
    client: booking?.client_name ? `<b>${booking.client_name}</b>` : ""
  }
  const getConfirmInfo = () => {
    if (refundType === "partial")
      return t("bookings.cancel_modal.confirm_info_partial", { ...confirmInfoParams, amount: `<b>${numberToCurrency(partialAmount)}</b>` })
    if (refundType === "full")
      return t("bookings.cancel_modal.confirm_info_full", { ...confirmInfoParams, amount: `<b>${numberToCurrency(breakdownData?.amount_paid_by_guest || 0)}</b>` })
    return t("bookings.cancel_modal.confirm_info_no_refund", confirmInfoParams)
  }
  const confirmInfo = getConfirmInfo()

  return (
    <>
      <Modal open={open} onCancel={onCancel} footer={null} width={620} title={t("bookings.cancel_modal.title")} destroyOnClose centered maskClosable={false}>
        <div className={styles.cancelBookingModalContent}>
          <BookingCard booking={booking} />
          <Divider className={styles.sectionDivider} />
          <ReasonSection
            cancelReasons={cancelReasons} reasonsLoading={reasonsLoading} reasonsFailed={reasonsFailed}
            selectedReason={selectedReason} setSelectedReason={setSelectedReason}
            reasonError={reasonError} setReasonError={setReasonError}
            isOtherSelected={isOtherSelected}
            otherReason={otherReason} setOtherReason={setOtherReason}
            otherReasonError={otherReasonError} setOtherReasonError={setOtherReasonError}
          />
          <Divider className={styles.sectionDivider} />
          <PaymentBreakdown
            breakdownData={breakdownData} breakdownLoading={breakdownLoading}
            refundType={refundType} partialAmount={partialAmount} partialAmountError={partialAmountError}
            isRefundDisabled={isRefundDisabled}
            handleRadioChange={handleRadioChange} handlePartialAmountChange={handlePartialAmountChange}
          />
        </div>
        <Flex gap={12} className={styles.modalFooter}>
          <Button variant="outlined" color="primary" onClick={onCancel} disabled={submitLoading}>
            {t("bookings.cancel_modal.cancel_button")}
          </Button>
          <Button color="danger" variant="solid" onClick={handleSubmitClick} disabled={submitLoading || breakdownLoading || !!partialAmountError} loading={submitLoading}>
            {t("bookings.cancel_modal.submit_button")}
          </Button>
        </Flex>
        {submitLoading && (
          <Flex justify="center" align="center" className={`position-absolute ${styles.modalLoaderWrapper}`}>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </Flex>
        )}
      </Modal>
      <ConfirmModal
        open={confirmOpen}
        title={t("bookings.cancel_modal.confirm_title")}
        info={confirmInfo}
        onCancel={() => setConfirmOpen(false)}
        onOk={handleConfirmClick}
        cancelLabel={t("bookings.cancel_modal.cancel_button")}
        okLabel={t("bookings.cancel_modal.confirm_button")}
      />
    </>
  )
}

CancelBookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      uuid: PropTypes.string,
      image: PropTypes.string,
      title: PropTypes.string,
      booking_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      booked_on: PropTypes.string,
      client_name: PropTypes.string,
      client_email: PropTypes.string
    })
  ).isRequired
}

export default CancelBookingModal
